// ========================================
// Firestore Database Utilities
// ========================================
// Helper functions for interacting with Firestore collections

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from './config';
import {
  UserProfile,
  CheckIn,
  Gratitude,
  Resentment,
  UserSettings,
} from '../types';

// ========================================
// User Profile Functions
// ========================================

export const createUserProfile = async (
  userId: string,
  userData: Partial<UserProfile>
): Promise<{ success: boolean }> => {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'info');
    await setDoc(profileRef, {
      ...userData,
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'info');
    const profileSnap = await getDoc(profileRef);

    if (profileSnap.exists()) {
      return profileSnap.data() as UserProfile;
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (
  userId: string,
  updates: Partial<UserProfile>
): Promise<{ success: boolean }> => {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'info');
    await updateDoc(profileRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// ========================================
// Check-ins Functions
// ========================================

export const createCheckIn = async (
  userId: string,
  checkInData: Omit<CheckIn, 'id' | 'timestamp'>
): Promise<{ success: boolean; id: string }> => {
  try {
    const checkInsRef = collection(db, 'users', userId, 'checkIns');
    const docRef = await addDoc(checkInsRef, {
      ...checkInData,
      timestamp: serverTimestamp(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating check-in:', error);
    throw error;
  }
};

export const getCheckIns = async (userId: string, limitCount = 50): Promise<CheckIn[]> => {
  try {
    const checkInsRef = collection(db, 'users', userId, 'checkIns');
    const q = query(checkInsRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);

    const checkIns: CheckIn[] = [];
    querySnapshot.forEach((doc) => {
      checkIns.push({ id: doc.id, ...doc.data() } as CheckIn);
    });

    return checkIns;
  } catch (error) {
    console.error('Error getting check-ins:', error);
    throw error;
  }
};

export const getRecentCheckIn = async (userId: string): Promise<CheckIn | null> => {
  try {
    const checkInsRef = collection(db, 'users', userId, 'checkIns');
    const q = query(checkInsRef, orderBy('timestamp', 'desc'), limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      if (docSnap) {
        return { id: docSnap.id, ...docSnap.data() } as CheckIn;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting recent check-in:', error);
    throw error;
  }
};

// ========================================
// Gratitude Functions
// ========================================

export const createGratitude = async (
  userId: string,
  gratitudeData: Omit<Gratitude, 'id' | 'dateAdded' | 'isActive'>
): Promise<{ success: boolean; id: string }> => {
  try {
    const gratitudesRef = collection(db, 'users', userId, 'gratitudes');
    const docRef = await addDoc(gratitudesRef, {
      ...gratitudeData,
      dateAdded: serverTimestamp(),
      isActive: true,
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating gratitude:', error);
    throw error;
  }
};

export const getGratitudes = async (userId: string): Promise<Gratitude[]> => {
  try {
    const gratitudesRef = collection(db, 'users', userId, 'gratitudes');
    const q = query(gratitudesRef, orderBy('dateAdded', 'desc'));
    const querySnapshot = await getDocs(q);

    const gratitudes: Gratitude[] = [];
    querySnapshot.forEach((doc) => {
      gratitudes.push({ id: doc.id, ...doc.data() } as Gratitude);
    });

    return gratitudes;
  } catch (error) {
    console.error('Error getting gratitudes:', error);
    throw error;
  }
};

export const updateGratitude = async (
  userId: string,
  gratitudeId: string,
  updates: Partial<Gratitude>
): Promise<{ success: boolean }> => {
  try {
    const gratitudeRef = doc(db, 'users', userId, 'gratitudes', gratitudeId);
    await updateDoc(gratitudeRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating gratitude:', error);
    throw error;
  }
};

export const deleteGratitude = async (
  userId: string,
  gratitudeId: string
): Promise<{ success: boolean }> => {
  try {
    const gratitudeRef = doc(db, 'users', userId, 'gratitudes', gratitudeId);
    await deleteDoc(gratitudeRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting gratitude:', error);
    throw error;
  }
};

// ========================================
// Resentment Functions
// ========================================

export const createResentment = async (
  userId: string,
  resentmentData: Omit<Resentment, 'id' | 'dateAdded' | 'status'>
): Promise<{ success: boolean; id: string }> => {
  try {
    const resentmentsRef = collection(db, 'users', userId, 'resentments');
    const docRef = await addDoc(resentmentsRef, {
      ...resentmentData,
      dateAdded: serverTimestamp(),
      status: 'active',
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating resentment:', error);
    throw error;
  }
};

export const getResentments = async (userId: string): Promise<Resentment[]> => {
  try {
    const resentmentsRef = collection(db, 'users', userId, 'resentments');
    const q = query(resentmentsRef, orderBy('dateAdded', 'desc'));
    const querySnapshot = await getDocs(q);

    const resentments: Resentment[] = [];
    querySnapshot.forEach((doc) => {
      resentments.push({ id: doc.id, ...doc.data() } as Resentment);
    });

    return resentments;
  } catch (error) {
    console.error('Error getting resentments:', error);
    throw error;
  }
};

export const updateResentment = async (
  userId: string,
  resentmentId: string,
  updates: Partial<Resentment>
): Promise<{ success: boolean }> => {
  try {
    const resentmentRef = doc(db, 'users', userId, 'resentments', resentmentId);

    // If status is being changed to 'released', add dateReleased
    if (updates.status === 'released' && !updates.dateReleased) {
      updates.dateReleased = serverTimestamp() as any;
    }

    await updateDoc(resentmentRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating resentment:', error);
    throw error;
  }
};

export const deleteResentment = async (
  userId: string,
  resentmentId: string
): Promise<{ success: boolean }> => {
  try {
    const resentmentRef = doc(db, 'users', userId, 'resentments', resentmentId);
    await deleteDoc(resentmentRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting resentment:', error);
    throw error;
  }
};

// ========================================
// Settings Functions
// ========================================

export const getUserSettings = async (userId: string): Promise<UserSettings> => {
  try {
    const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
    const settingsSnap = await getDoc(settingsRef);

    if (settingsSnap.exists()) {
      return settingsSnap.data() as UserSettings;
    }

    // Return default settings if none exist
    return {
      guidingPrincipleTerm: 'Inner Wisdom',
    };
  } catch (error) {
    console.error('Error getting user settings:', error);
    throw error;
  }
};

export const updateUserSettings = async (
  userId: string,
  settings: Partial<UserSettings>
): Promise<{ success: boolean }> => {
  try {
    const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
    await updateDoc(settingsRef, settings);
    return { success: true };
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw error;
  }
};
